import { ar, en } from '@outss/i18n';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from './api';
import { nameFor } from './arabic';
import { i18n, useLanguage } from './i18n';
import { useOrgId } from './org';

export interface Account {
  /** Arabic name (ADR-0025), or null: show `name`. */
  nameAr?: string | null;
  /** Parent:child path in Arabic, each part falling back to English. */
  fullNameAr?: string;
  accountTypeNameAr?: string | null;
  detailTypeNameAr?: string | null;
  descriptionAr?: string | null;
  id: string;
  code: string;
  name: string;
  fullName: string;
  class: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
  accountType: string;
  accountTypeName: string;
  detailType: string;
  detailTypeName: string;
  parentId: string | null;
  depth: number;
  description: string | null;
  defaultTaxCodeId: string | null;
  defaultTaxCode: string | null;
  isControl: boolean;
  systemKey: string | null;
  isActive: boolean;
  hasTransactions: boolean;
  balance: string;
  currency: string;
  /** The account's own currency (foreign Bank and Credit card accounts). */
  accountCurrency: string;
  /** Balance in the account's own currency when it is foreign, else null. */
  foreignBalance: string | null;
}

export interface AccountCategory {
  key: string;
  name: string;
  class: string;
  normalBalance: 'debit' | 'credit';
  report: string;
  nameAr?: string | null;
  detailTypes: Array<{
    key: string;
    name: string;
    nameAr?: string | null;
    description: string | null;
    descriptionAr?: string | null;
    isControl: boolean;
  }>;
}

export interface TaxCode {
  id: string;
  code: string;
  name: string;
  /** Arabic name (ADR-0025); the UAE pack codes ship with one. */
  nameAr?: string | null;
  kind: 'standard' | 'zero' | 'exempt' | 'out_of_scope' | 'reverse_charge';
  /** Percent as a decimal string ("5.00"). */
  rate: string;
  appliesTo: 'sales' | 'purchases' | 'both';
  returnBox: string | null;
  /** India GST: CGST + SGST or IGST. */
  gstSplit?: 'cgst_sgst' | 'igst' | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  description: string | null;
  isActive: boolean;
  inUse: boolean;
}

export function useAccounts(opts: { includeInactive?: boolean } = {}) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['accounts', orgId, opts.includeInactive ?? false],
    queryFn: () =>
      api<Account[]>(
        `/orgs/${orgId}/accounts${opts.includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useAccountTypes() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['account-types', orgId],
    queryFn: () => api<AccountCategory[]>(`/orgs/${orgId}/account-types`),
    enabled: Boolean(orgId),
    staleTime: Infinity,
  });
}

export function useTaxCodes(opts: { includeInactive?: boolean } = {}) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['tax-codes', orgId, opts.includeInactive ?? false],
    queryFn: () =>
      api<TaxCode[]>(
        `/orgs/${orgId}/tax-codes${opts.includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

export const TAX_KIND_LABELS: Record<TaxCode['kind'], string> = {
  standard: 'Standard rated',
  zero: 'Zero rated',
  exempt: 'Exempt',
  out_of_scope: 'Out of scope',
  reverse_charge: 'Reverse charge',
};

/** VAT 201 boxes a code reports to (document 07). */
export const RETURN_BOXES: Array<{ value: string; label: string }> = [
  { value: '1', label: '1a–1g · Standard-rated supplies (by emirate)' },
  { value: '2', label: '2 · Tax refunds to tourists' },
  { value: '3', label: '3 · Supplies subject to reverse charge' },
  { value: '4', label: '4 · Zero-rated supplies' },
  { value: '5', label: '5 · Exempt supplies' },
  { value: '6', label: '6 · Goods imported into the UAE' },
  { value: '7', label: '7 · Adjustments to goods imported' },
  { value: '9', label: '9 · Standard-rated expenses' },
  { value: '10', label: '10 · Reverse charge (recoverable)' },
];

/**
 * A tax code's description in the reader's language. Tax codes have no Arabic description field:
 * the descriptions the UAE and India packs seed are in the catalogue by code, and are shown in
 * Arabic only while the code still has that exact English text (an edited or user-made
 * description is shown as typed).
 */
export function taxCodeDescription(
  tc: Pick<TaxCode, 'code' | 'description'>,
  lang: 'en' | 'ar' = i18n.language === 'ar' ? 'ar' : 'en',
): string | null {
  const d = tc.description;
  if (!d || lang !== 'ar') return d;
  for (const part of ['taxCodeDescription', 'taxCodeDescriptionBefore2025'] as const) {
    const english = (en.accountingPages[part] as Record<string, string>)[tc.code];
    const arabic = (ar.accountingPages[part] as Record<string, string>)[tc.code];
    if (english === d && arabic) return arabic;
  }
  return d;
}

/**
 * Account names in the reader's language, by account id, for responses that carry only the
 * English name (bank accounts): falls back to the English given when the account is not in the
 * active chart.
 */
export function useAccountNames() {
  const accounts = useAccounts();
  const lang = useLanguage();
  return useMemo(() => {
    const byId = new Map((accounts.data ?? []).map((a) => [a.id, a]));
    return {
      name: (id: string, english: string) => nameFor(lang, english, byId.get(id)?.nameAr),
      detailTypeName: (id: string, english: string) =>
        nameFor(lang, english, byId.get(id)?.detailTypeNameAr),
      /** "1000 Bank – current account" as the API joined it, with the Arabic name when there is one. */
      codeName: (id: string | null, english: string) => {
        const a = id ? byId.get(id) : undefined;
        return lang === 'ar' && a?.nameAr ? `${a.code} ${a.nameAr}` : english;
      },
      /** Both names, for a combobox's search keywords. */
      keywords: (id: string, english: string) => `${english} ${byId.get(id)?.nameAr ?? ''}`,
    };
  }, [accounts.data, lang]);
}

/** "5%" or "5.5%" (document 05, Percent). */
export const formatRate = (rate: string): string =>
  `${rate.includes('.') ? rate.replace(/\.?0+$/, '') : rate}%`;

/** Codes for a sales or purchase picker. */
export const taxCodesFor = (codes: TaxCode[] | undefined, side: 'sales' | 'purchases') =>
  (codes ?? []).filter((t) => t.isActive && (t.appliesTo === 'both' || t.appliesTo === side));

/** Opening balance of a Bank or Credit card account (document 06 A1a, ADR-0023). */
export interface OpeningBalance {
  accountId: string;
  /** Natural sign ("-200.00" = overdrawn bank); null when none is set. */
  amount: string | null;
  date: string | null;
  entryId: string | null;
  entryNo: string | null;
  currency: string;
  suggestedDate: string;
}

/** Bank and Credit card accounts in the home currency take an opening balance here. */
export const takesOpeningBalance = (
  a: Pick<Account, 'accountType' | 'accountCurrency' | 'currency' | 'isActive'>,
) =>
  (a.accountType === 'bank' || a.accountType === 'credit_card') &&
  a.accountCurrency === a.currency &&
  a.isActive;

export function useOpeningBalance(accountId: string | null) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['accounts', orgId, 'opening', accountId],
    queryFn: () => api<OpeningBalance>(`/orgs/${orgId}/accounts/${accountId}/opening-balance`),
    enabled: Boolean(orgId && accountId),
  });
}
